<template>
  <AppSidebar>
    <div class="space-y-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Order History</h1>
          <p class="text-gray-600">View your order history and status</p>
        </div>
        <router-link to="/order-service" class="btn btn-primary">
          New Order
        </router-link>
      </div>
      
      <!-- Order Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div class="card p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <OrdersIcon class="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Total Orders</dt>
                <dd class="text-lg font-medium text-gray-900">{{ userOrders.length }}</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div class="card p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <ActiveIcon class="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Active</dt>
                <dd class="text-lg font-medium text-gray-900">{{ activeOrders.length }}</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div class="card p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <CompletedIcon class="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Completed</dt>
                <dd class="text-lg font-medium text-gray-900">{{ completedOrders.length }}</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div class="card p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <MoneyIcon class="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Total Spent</dt>
                <dd class="text-lg font-medium text-gray-900">${{ totalSpent.toLocaleString() }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Filter and Search -->
      <div class="flex items-center space-x-4">
        <div class="flex-1">
          <input
            v-model="searchQuery"
            type="text"
            class="form-input"
            placeholder="Search orders..."
          />
        </div>
        <select v-model="statusFilter" class="form-input">
          <option value="">All Status</option>
          <option value="pending_review">Pending Review</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      
      <!-- Orders List -->
      <div class="space-y-6">
        <div v-if="filteredOrders.length === 0" class="text-center py-12">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <OrdersIcon class="w-8 h-8 text-gray-400" />
          </div>
          <p class="text-gray-500 mb-4">No orders found</p>
          <router-link to="/order-service" class="btn btn-primary">
            Place Your First Order
          </router-link>
        </div>
        
        <div 
          v-for="order in filteredOrders" 
          :key="order.id"
          class="card p-6 hover:shadow-lg transition-shadow duration-200"
        >
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-1">
                {{ order.order_number }}
              </h3>
              <p class="text-gray-600">{{ getServiceName(order.service_id) }}</p>
              <p class="text-sm text-gray-500">
                Ordered on {{ formatDate(order.created_at) }}
              </p>
            </div>
            
            <div class="text-right">
              <span 
                class="inline-flex px-3 py-1 text-sm font-medium rounded-full"
                :class="getStatusClass(order.status)"
              >
                {{ formatStatus(order.status) }}
              </span>
              <p class="text-lg font-bold text-gray-900 mt-2">
                ${{ order.total_amount?.toLocaleString() || '0' }}
              </p>
            </div>
          </div>
          
          <!-- Progress Bar -->
          <div class="mb-4" v-if="order.status !== 'cancelled'">
            <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{{ getProgressPercentage(order) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div 
                class="bg-primary-600 h-2 rounded-full transition-all duration-300"
                :style="{ width: getProgressPercentage(order) + '%' }"
              ></div>
            </div>
          </div>
          
          <!-- Order Details -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <!-- Requirements -->
            <div v-if="order.requirements?.project_description">
              <h4 class="font-medium text-gray-900 mb-2">Project Description</h4>
              <p class="text-gray-600 text-sm">{{ order.requirements.project_description }}</p>
            </div>
            
            <!-- Timeline -->
            <div v-if="order.timeline?.milestones?.length">
              <h4 class="font-medium text-gray-900 mb-2">Current Milestones</h4>
              <div class="space-y-2">
                <div 
                  v-for="milestone in order.timeline.milestones.slice(0, 3)" 
                  :key="milestone.name"
                  class="flex items-center justify-between text-sm"
                >
                  <div class="flex items-center space-x-2">
                    <div 
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-green-500': milestone.status === 'completed',
                        'bg-blue-500': milestone.status === 'in_progress',
                        'bg-gray-300': milestone.status === 'pending'
                      }"
                    ></div>
                    <span class="text-gray-700">{{ milestone.name }}</span>
                  </div>
                  <span class="text-gray-500">{{ formatDate(milestone.date) }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Recent Communication -->
          <div v-if="order.communication?.length" class="mb-6">
            <h4 class="font-medium text-gray-900 mb-3">Recent Updates</h4>
            <div class="bg-gray-50 rounded-lg p-4">
              <div 
                v-for="comm in order.communication.slice(-2)" 
                :key="comm.date"
                class="mb-3 last:mb-0"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="text-sm font-medium text-gray-900 capitalize">{{ comm.author }}</span>
                  <span class="text-xs text-gray-500">{{ formatDate(comm.date) }}</span>
                </div>
                <p class="text-sm text-gray-700">{{ comm.message }}</p>
              </div>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            <div class="flex space-x-4">
              <button 
                @click="viewOrderDetails(order)"
                class="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View Details
              </button>
              <button 
                v-if="['pending', 'processing'].includes(order.status)"
                @click="contactSupport(order)"
                class="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Contact Support
              </button>
              <router-link
                v-if="['completed', 'processing'].includes(order.status)"
                :to="`/orders/${order.id}/invoice`"
                class="text-green-600 hover:text-green-700 text-sm font-medium"
              >
                View Invoice
              </router-link>
            </div>
            
            <div class="flex space-x-2">
              <router-link
                v-if="['approved', 'quote_sent'].includes(order.status)"
                :to="`/orders/${order.id}/pay`"
                class="btn btn-primary btn-sm"
              >
                Pay Now
              </router-link>
              <button 
                v-if="order.status === 'pending'"
                @click="cancelOrder(order)"
                class="btn btn-outline btn-sm text-red-600 border-red-300 hover:bg-red-50"
              >
                Cancel
              </button>
              <button 
                v-if="order.status === 'completed'"
                @click="reorder(order)"
                class="btn btn-primary btn-sm"
              >
                Reorder
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/orders'
import { useServiceStore } from '@/stores/services'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const router = useRouter()
const authStore = useAuthStore()
const orderStore = useOrderStore()
const serviceStore = useServiceStore()
const uiStore = useUIStore()

// State
const searchQuery = ref('')
const statusFilter = ref('')

// Initialize
onMounted(async () => {
  await orderStore.fetchOrders()
  await serviceStore.fetchServices()
})

// Computed
const userOrders = computed(() => {
  return orderStore.orders
})

const filteredOrders = computed(() => {
  let orders = userOrders.value
  
  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    orders = orders.filter(order => 
      order.order_number.toLowerCase().includes(query) ||
      getServiceName(order.service_id).toLowerCase().includes(query)
    )
  }
  
  // Status filter
  if (statusFilter.value) {
    orders = orders.filter(order => order.status === statusFilter.value)
  }
  
  return orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const activeOrders = computed(() => {
  return userOrders.value.filter(order => 
    !['completed', 'cancelled'].includes(order.status)
  )
})

const completedOrders = computed(() => {
  return userOrders.value.filter(order => order.status === 'completed')
})

const totalSpent = computed(() => {
  return completedOrders.value.reduce((sum, order) => 
    sum + (order.total_amount || 0), 0
  )
})

// Methods
const getServiceName = (serviceId) => {
  const service = serviceStore.getServiceById(serviceId)
  return service?.name || 'Unknown Service'
}

const getProgressPercentage = (order) => {
  if (!order.timeline?.milestones?.length) return 0
  
  const completed = order.timeline.milestones.filter(m => m.status === 'completed').length
  const total = order.timeline.milestones.length
  return Math.round((completed / total) * 100)
}

const getStatusClass = (status) => {
  const classes = {
    'pending_review': 'bg-yellow-100 text-yellow-800',
    'quote_sent': 'bg-blue-100 text-blue-800',
    'approved': 'bg-green-100 text-green-800',
    'in_progress': 'bg-purple-100 text-purple-800',
    'review': 'bg-orange-100 text-orange-800',
    'completed': 'bg-green-100 text-green-800',
    'cancelled': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const formatStatus = (status) => {
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const viewOrderDetails = (order) => {
  router.push(`/orders/${order.id}`)
}

const contactSupport = (order) => {
  const message = `Hi! I need support for my Order #${order.order_number}.\n\n` +
    `📋 *Service:* ${getServiceName(order.service_id)}\n` +
    `📍 *Status:* ${formatStatus(order.status)}\n\n` +
    `Please help me with...`
  orderStore.openWhatsApp(message)
}

const cancelOrder = async (order) => {
  if (confirm(`Are you sure you want to cancel order ${order.order_number}?`)) {
    await orderStore.cancelOrder(order.id, 'Cancelled by client')
  }
}

const reorder = (order) => {
  // Navigate to order service with pre-filled data
  router.push('/order-service')
  
  uiStore.showNotification({
    type: 'info',
    title: 'Reorder',
    message: 'Service order form opened with previous order details'
  })
}
</script>

<script>
// Icon components
export default {
  components: {
    OrdersIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
      `
    },
    ActiveIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
      `
    },
    CompletedIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `
    },
    MoneyIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
        </svg>
      `
    }
  }
}
</script>
